import {
  VictoryChart,
  VictoryAxis,
  VictoryScatter,
  VictoryTheme,
  VictoryLabel,
  VictoryTooltip,
  createContainer,
  VictoryArea,
} from 'victory';
import {useEffect, useMemo, useState, useRef} from 'react';
// d3-time stuff
import {useMediaQuery, useTheme} from '@mui/material';
import {useResponsiveStyles} from '../../utils/responsiveness';
import {timeDay, timeDays, timeMonths, timeMonth, timeWeek, timeYear, timeMonday, timeHour, timeMinute} from 'd3-time';
import {delay, roundToDecimal} from '../../utils/miscellaneous';
import {handleFormattingByMetricKey} from './vitalsList';

const VictoryZoomVoronoiContainer = createContainer('zoom', 'voronoi');

const directionTextDisplayMap = {
  right: 'future',
  left: 'past',
};

const LabelButton = ({
  name,
  direction,
  chartHeight,
  chartWidth,
  isDataIn,
  getEntireDomain,
  timeRange,
  chartData,
  zoomedXDomain,
  setZoomedXDomain,
  baseFontSize,
}) => {
  const theme = useTheme();
  const r = useResponsiveStyles(theme);

  const isDataInCurrentZoomedDomain = isDataIn === undefined ? true : isDataIn.currentZoomedDomain;

  const zoomedXDomainDirections = {
    left: isDataIn === undefined ? false : isDataIn.leftOfZoomedDomain,
    right: isDataIn === undefined ? false : isDataIn.rightOfZoomedDomain,
  };
  const isDataToTheDirectionOfZoomedDomain = zoomedXDomainDirections[direction];
  const horizontalDistance = r({xs: 50, sm: 80, md: 100});

  const chevronsPositions = useMemo(() => {
    return {
      y: chartHeight - 37,
      x: {
        left: r({
          xs: 25,
          sm: 13,
          md: 20,
          lg: 31,
        }),
        right: r({
          xs: chartWidth - horizontalDistance,
          sm: chartWidth - horizontalDistance + 45,
          md: chartWidth - horizontalDistance + 55,
          lg: chartWidth - horizontalDistance + 45,
        }),
      },
      z: {
        left: -90,
        right: 90,
      },
    };
  }, [direction, chartHeight, r]);

  const transformProperty = useMemo(() => {
    if (direction === 'left')
      return {
        transform: `rotate(${chevronsPositions.z.left},12,12) translate(-${chevronsPositions.y},${chevronsPositions.x.left})`,
      };
    return {
      transform: `translate(${chevronsPositions.x.right},${chevronsPositions.y}) rotate(${chevronsPositions.z.right},12,12)`,
    };
  }, [direction, chartHeight, chevronsPositions]);

  return (
    <g
      onClick={() => {
        let value;
        if (direction === 'left') value = chartData.findLast(d => d.x < zoomedXDomain[0]).x;
        if (direction === 'right') value = chartData.find(d => d.x > zoomedXDomain[1]).x;

        const [first, last] = zoomedDomainOnReferencePoint(chartData, getEntireDomain, timeRange, value);
        setZoomedXDomain([first, last]);
      }}
      style={{
        cursor: 'pointer',
        display: !isDataToTheDirectionOfZoomedDomain || isDataInCurrentZoomedDomain ? 'none' : undefined,
      }}
    >
      <g
        fill="none"
        stroke="grey"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        {...transformProperty}
      >
        <path d="M6 18L12 12 18 18"></path>
      </g>
      <VictoryLabel
        name={name}
        x={direction === 'left' ? horizontalDistance : direction === 'right' ? chartWidth - horizontalDistance : 200}
        y={chartHeight - 25}
        text={`Go to nearest\n${directionTextDisplayMap[direction]} value`}
        textAnchor="middle"
        style={{
          fontSize: baseFontSize - r({xs: 3, sm: 2}),
          fill: 'grey',
          fontFamily: 'Poppins, sans-serif',
          display: r({
            xs: 'none',
            sm: undefined,
          }),
        }}
      />
    </g>
  );
};

/** METRICS CHART HELPER FUNCTIONS  **/

/**
 * formatVoronoiDateLabelBasedOnTimeRange
 * Formats the hovered tooltip date text based on the current time range and the date of the metric value
 *
 * @param {string} timeRange - valid time range in the MetricsChart component prop: ["day","week","month","year"]
 * @param {Date} date - Date object of the desired to format date
 *
 * @returns {string} Formatted date string
 */
const formatVoronoiDateLabelBasedOnTimeRange = (timeRange, date) => {
  if (timeRange === 'year') date = timeMonth.round(date);

  const timeRanges = {
    day: date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }),
    week: date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    month: date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    year: date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
    }),
  };

  return timeRanges[timeRange] || `not valid time range: ${timeRange}`;
};

/**
 * Default Zoomed Domain Range of hours when the user clicks a time range in the chart
 * *When the user clicks another time range, obviously the chart will have to mutate its data to adapt to that time range conditions, one of those conditions is the domain's range. This is a way to handle the default domain range hours based on the timeRange
 *
 * day:26
 * week: 200
 * month: 330
 * year: 8900
 */
const defaultZoomedDomainsBasedOnTimeRange = {
  day: 26,
  week: 200,
  month: 330,
  year: 8900, //approximately one year
};

/**
 * Calculates the right most point for a zoomed domain given a timeRange that will be passed as a key
 * *the offsets are used so that the minimum/maximum points have a little padding and are not right at the ends of the x axis
 */
const possibleRightMostPoints = {
  day: date => timeMinute.offset(timeDay.ceil(date), 20),
  week: date => timeHour.offset(timeWeek.ceil(date), 4),
  month: date => {
    // in case the last point is precissely the beginning of another month, adding one day so that the whole other month can render
    if (date.getDate() === 1) date = timeDay.offset(date, 1);

    return timeHour.offset(timeMonth.ceil(date), -4);
  },
  year: date => {
    // in case the last point is precissely the beginning of another year, adding one month so that the whole other year can render
    if (date.getMonth() === 0) date = timeMonth.offset(date, 1);
    return timeHour.offset(timeYear.ceil(date), -12);
  },
};

/**
 * Calculates the left most point for a zoomed domain given a timeRange that will be passed as a key
 */
const possibleLeftMostPoints = {
  day: date => timeMinute.offset(timeDay.floor(date), -30),
  week: date => timeHour.offset(timeMonday.floor(date), -2),
  month: date => timeHour.offset(timeMonth.floor(date), -4),
  year: date => timeDay.offset(timeYear.floor(date), -4),
};

/**
 * formatXAxisTicksBasedOnTimeRange
 * Formats the tick values from the x axis of the chart based on the time range
 *
 * @param {string} timeRange - valid time range in the MetricsChart component prop: ["day","week","month","year"]
 * @param {Date} date - Date object of the desired to format date(tick value in this case)
 *
 * @returns {string} Formatted date string
 */
const formatXAxisTicksBasedOnTimeRange = (timeRange, dateObject) => {
  const formatBasedOnTimeRange = {
    day: dateObject.toLocaleTimeString(undefined, {
      hour12: true,
      hour: 'numeric',
    }),
    week: dateObject.toLocaleDateString(undefined, {
      weekday: 'short',
    }),
    month: dateObject
      .toLocaleDateString(undefined, {
        day: 'numeric',
      })
      .padStart(2, '0'),
    year: dateObject.toLocaleDateString(undefined, {
      month: 'short',
    }),
  };

  return formatBasedOnTimeRange[timeRange] || dateObject;
};

/**
 * zoomedDomainOnReferencePoint
 * calculates a zoomed domain on the chart based on the current time range and a date value that must be shown. That zoomed domain is outputted in the form of a Date array of 2 elements, the first being the oldest value and the last being the newest value, both are going to be the boundaries of the zoomed domain
 *
 * @param {Array.<{
 *  x: Date // Date
 *  y: Number // Numeric metric value
 * }>} chartData - elements that this current chart is displaying. must be sorted in terms of x
 * @param {Date[]} maximumDomain - Array of 2 values being the maximum boundaries of the chart domain
 * @param {string} timeRange - valid time range in the MetricsChart component prop: ["day","week","month","year"]
 * @param {Date} customReferencePoint - Date object which should be within the maximumDomain boundaries and represents a point in time which you want to the chart to display within its zoomed domain
 *
 * @returns {Date[]} Date array of 2 elements, the first being the oldest value and the last being the newest value, both are going to be the boundaries of the zoomed domain
 */
const zoomedDomainOnReferencePoint = (chartData, maximumDomain, timeRange, customReferencePoint) => {
  const referencePoint = customReferencePoint === undefined ? chartData[chartData.length - 1].x : customReferencePoint;
  let rightMostPoint = possibleRightMostPoints[timeRange](referencePoint);
  const distance = defaultZoomedDomainsBasedOnTimeRange[timeRange];
  let leftMostPoint = timeHour.offset(rightMostPoint, -distance);

  // if the calculated zoomed domain is greater than the boundaries of the entire domain, then we display the entire domain, since the entire domain is also based on the possible right/left most points then it won't cause errors (i hope)
  if (
    (rightMostPoint > maximumDomain.x[1] || leftMostPoint < maximumDomain.x[0]) &&
    customReferencePoint !== referencePoint
  ) {
    return [maximumDomain.x[0], maximumDomain.x[1]];
  }

  if (customReferencePoint === referencePoint) {
    rightMostPoint = rightMostPoint > maximumDomain.x[1] ? maximumDomain.x[1] : rightMostPoint;
    leftMostPoint = leftMostPoint < maximumDomain.x[0] ? maximumDomain.x[0] : leftMostPoint;
  }

  if (referencePoint === chartData[0].x && timeRange === 'month') {
    //comparing in which of those scenarios(left or right as the base) will the referencePoint actually appear
    const leftPointBase = possibleLeftMostPoints[timeRange](referencePoint);
    const rightBasedOnLeft = timeHour.offset(leftPointBase, distance);

    const rightPointBase = possibleRightMostPoints[timeRange](referencePoint);
    const leftBasedOnRight = timeHour.offset(rightPointBase, -distance);

    if (referencePoint >= leftPointBase && referencePoint <= rightBasedOnLeft) {
      leftMostPoint = leftPointBase;
      rightMostPoint = rightBasedOnLeft;
    } else {
      leftMostPoint = leftBasedOnRight;
      rightMostPoint = rightPointBase;
    }

    return [leftMostPoint, rightMostPoint];
  }
  // exception with months so it looks okay
  if (timeRange === 'month' && referencePoint < leftMostPoint && referencePoint !== chartData[0].x) {
    rightMostPoint = timeHour.offset(referencePoint, distance / 2);

    leftMostPoint = timeHour.offset(referencePoint, -distance / 2);

    if (leftMostPoint < maximumDomain.x[0]) {
      leftMostPoint = possibleLeftMostPoints[timeRange](maximumDomain.x[0]);
      rightMostPoint = timeHour.offset(leftMostPoint, distance);
    }
  }
  return [leftMostPoint, rightMostPoint];
};

/**
 * Metric Chart Component
 * Chart used to display metric values in a timely way in the dashboard. By a timely way it means that based on the "time range" the data will be displayed and rendered in a different way(data mutations depending on time range and showing different x axis values). When hovering each value on the chart, a tooltip will appear for more detail
 *
 * @param {Object} props - Component props
 * @param {String} props.timeRange - current time range of the chart. acceptable values are ["day","week","month","year"]. Must be a getter from a useState hook for this to work correctly
 * @param {function} props.setTimeRange - setter from props.timeRange useState hook
 * @param {String} props.metricUnit - Unit to be shown with metric values on the chart
 * @param {String} props.metricKey - Identifier of that metric
 * @param {Array.<{
 *   x: Date // Date Object
 *   y: Number // Metric numeric value
 * }>} props.wholeData - Array of objects with a particular interface that will be used for the chart values, x-axis and y-axis.Data must be sorted in terms of x
 * @param {Number} props.chartHeight - height of the whole component
 * @param {Number} props.chartWidth - width of the whole component
 *
 * @returns {React.Component} Rendered Chart
 */
export const MetricsChart = ({timeRange, setTimeRange, metricUnit, metricKey, wholeData, chartHeight, chartWidth}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const r = useResponsiveStyles(theme);
  // fontsize variables
  const victoryAxesFontSizes = r({
    xs: timeRange !== 'year' ? 14 : 12,
    sm: 16,
    md: 19,
    lg: 15,
  });
  const dateRangeLabelFontSizes = r({
    xs: 18,
    sm: 22,
    md: 23,
    lg: 20,
  });
  const tooltipFontSizes = r({
    xs: 16,
    md: 20,
  });
  const dataMutationClarificationFontSizes = r({
    // xs: 22,
    xs: 14,
    sm: 15,
    md: 18,
    lg: 15,
  });
  // scatter size of points, active will be 3 points more than this
  const victoryScatterSizes = r({
    xs: 4,
    sm: 6,
    lg: 7,
  });
  /**
   * An object containing time range limits, in terms of hours.
   * *That is, if the zoomed domain's range(in hours) is greater than those maximums, the timeRange will change to the upper timeRange value
   * This object is used in timeRangeDomains
   *
   * DAY\_MAX: 90
   * WEEK\_MAX: 300
   * MONTH\_MAX: 2000
   */
  const timeRangeLimits = {
    DAY_MAX: r({
      xs: 50,
      sm: 90,
      lg: 65,
    }),
    WEEK_MAX: r({
      xs: 250,
      sm: 300,
    }),
    MONTH_MAX: 2000,
  };
  /**
   * Determines if an amount of hours (assuming its from the absolute difference of the zoomed domain boundaries in terms of hours) is in the accepted range for an specific timeRange. You passed the time range as a key, execute it and it will tell you
   */
  const timeRangeDomains = {
    day: hours => hours <= timeRangeLimits['DAY_MAX'],
    week: hours => hours > timeRangeLimits['DAY_MAX'] && hours <= timeRangeLimits['WEEK_MAX'],
    month: hours => hours > timeRangeLimits['WEEK_MAX'] && hours <= timeRangeLimits['MONTH_MAX'],
    year: hours => {
      return hours >= timeRangeLimits['MONTH_MAX'];
    },
  };
  const nullPointValue = {x: '', y: ''};
  const [selectedPoint, setSelectedPoint] = useState(nullPointValue);

  const domainPadding = useMemo(() => {
    //left padding only since the right one is being handled by the tick values
    const timeRanges = {
      day: [0, 0],
      week: [0, 0.5],
      month: [0, 0],
      year: [10, 0],
    };
    return timeRanges[timeRange];
  }, [timeRange]);

  const chartData = useMemo(() => {
    if (timeRange === 'day') return [...wholeData];

    if (timeRange === 'year') {
      const groupedData = wholeData.reduce((acc, obj) => {
        const yearMonthKey = `${obj.x.getFullYear()}-${obj.x.getMonth() + 1}`;

        if (!acc[yearMonthKey]) {
          acc[yearMonthKey] = {
            x: new Date(yearMonthKey),
            y: obj.y,
            count: 1,
          };
        } else {
          acc[yearMonthKey].y += obj.y;
          acc[yearMonthKey].count++;
        }
        return acc;
      }, {});

      return Object.values(groupedData).map(({x, y, count}) => {
        const output = {x, y: Math.round(y / count)};
        // certain keys won't be rounded as integers because they are really small
        if (/^(stroke_risk)|(cardiovascular_risk)$/.test(metricKey)) {
          output.y = roundToDecimal(y / count, 2);
        } else if (/^heart_disease_risk$/.test(metricKey)) {
          output.y = roundToDecimal(y / count, 4);
        }

        return output;
      });
    }

    //both month and week are daily averages
    const groupedData = wholeData.reduce((acc, obj) => {
      const yearMonthDayKey = obj.x.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      if (!acc[yearMonthDayKey]) {
        acc[yearMonthDayKey] = {
          x: new Date(yearMonthDayKey),
          y: obj.y,
          count: 1,
        };
      } else {
        acc[yearMonthDayKey].y += obj.y;
        acc[yearMonthDayKey].count++;
      }
      return acc;
    }, {});

    const newData = Object.values(groupedData).map(({x, y, count}) => {
      const output = {x, y: Math.round(y / count)};
      // certain keys won't be rounded as integers because they are really small
      if (/^(stroke_risk)|(cardiovascular_risk)$/.test(metricKey)) {
        output.y = roundToDecimal(y / count, 2);
      } else if (/^heart_disease_risk$/.test(metricKey)) {
        output.y = roundToDecimal(y / count, 4);
      }

      return output;
    });

    return newData;
  }, [timeRange]);

  const prevTimeRange = useRef();
  const [isMouseDown, setIsMouseDown] = useState(false);

  useEffect(() => {
    const handleMouseDown = () => setIsMouseDown(true);
    const handleMouseUp = () => setIsMouseDown(false);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    prevTimeRange.current = timeRange;
  }, [timeRange]);

  const getEntireDomain = useMemo(() => {
    const yValues = wholeData.map(d => d.y);
    const maxX = chartData[chartData.length - 1].x;
    const minX = chartData[0].x;
    const maxY = Math.max(...yValues);

    const range = [Math.min(...yValues), maxY + (maxY % 5)];
    //in case the range is only 0
    if (range[0] === 0) {
      range[0] = -0.1;
      range[1] = range[1] > 1 ? range[1] : 1;
    }

    const output = {
      x: [possibleLeftMostPoints[timeRange](minX), possibleRightMostPoints[timeRange](maxX)],
      y: range,
    };

    return output;
  }, [wholeData, chartData]);
  // Clicking a button will take consider the zoom domain to move based on
  // the last point as the rightmost part of the zoomedXDomain
  useEffect(() => {
    if (
      timeRangeDomains[timeRange](zoomDomainTotalHours) &&
      zoomedXDomain[1] <= getEntireDomain.x[1] &&
      zoomedXDomain[0] >= getEntireDomain.x[0]
    ) {
      return;
    }
    // in this case is based on first point
    if (zoomedXDomain[0] < getEntireDomain.x[0] && timeRangeDomains[timeRange](zoomDomainTotalHours)) {
      const firstPoint = chartData[0].x;
      const leftMostPoint = possibleLeftMostPoints[timeRange](firstPoint);
      const rightMostPoint = timeHour.offset(leftMostPoint, defaultZoomedDomainsBasedOnTimeRange[timeRange]);
      setZoomedXDomain([leftMostPoint, rightMostPoint]);

      return;
    }
    // rightmost point for now
    const [leftMostPoint, rightMostPoint] = zoomedDomainOnReferencePoint(chartData, getEntireDomain, timeRange);

    setZoomedXDomain([leftMostPoint, rightMostPoint]);
  }, [timeRange, getEntireDomain]);

  const initialZoomDomain = useMemo(() => {
    return zoomedDomainOnReferencePoint(chartData, getEntireDomain, timeRange);
  }, []);

  const [zoomedXDomain, setZoomedXDomain] = useState(initialZoomDomain);
  const [timestampOfLastTimeRangeChange, setTimestampOfLastTimeRangeChange] = useState(null);

  const renderedData = useMemo(() => {
    const dataSubset = chartData.filter(d => d.x <= zoomedXDomain[0]);
    let startIndex = dataSubset.length - 1;
    if (startIndex === -1) startIndex = 0;
    let endIndex = chartData.findIndex(d => d.x > zoomedXDomain[1]);
    if (endIndex === -1) endIndex = chartData.length;
    const filtered = chartData.slice(startIndex, endIndex + 1);
    return filtered;
  }, [zoomedXDomain, chartData]);

  const tickValues = useMemo(() => {
    const isFirstPointPresent = renderedData[0].x === chartData[0].x; // if the lowest value is present, then its day/week/month/year will be rendered
    const isLastPointPresent = renderedData[renderedData.length - 1].x === chartData[chartData.length - 1].x;
    // if the greatest x value is present in the rendered data then the end of that time frame(day,week,month,year) will be rendered as well as tick labels
    const maxValue = isLastPointPresent
      ? possibleRightMostPoints[timeRange](renderedData[renderedData.length - 1].x)
      : renderedData[renderedData.length - 1].x;

    const minValue = isFirstPointPresent ? possibleLeftMostPoints[timeRange](renderedData[0].x) : renderedData[0].x;

    const approxDomainHours = Math.round(Math.abs(zoomedXDomain[0] - zoomedXDomain[1]) / (1000 * 60 * 60));

    // first index is the start date, last index is the end date
    const allCasesStartAndEndDate = {
      year: [timeMonth(minValue), timeMonth.offset(maxValue)],
      month: [timeDay(minValue), timeDay.offset(maxValue)],
      week: [timeDay(minValue), timeDay.offset(maxValue)],
      day: [timeHour(minValue), timeHour.offset(maxValue)],
    };

    let [startDate, endDate] = allCasesStartAndEndDate[timeRange];
    let lastPoint = chartData[chartData.length - 1].x;
    const allTickPossibleValues = {
      year: () => {
        let tickInterval = approxDomainHours > 10300 ? 2 : 1;
        if (isSmallScreen && approxDomainHours > 7000) tickInterval = 2;

        return timeMonths(startDate, endDate, tickInterval);
      },
      month: () => {
        endDate = possibleRightMostPoints[timeRange](lastPoint);
        let tickInterval = approxDomainHours > 800 ? 5 : approxDomainHours > 500 ? 3 : 1;

        if (isSmallScreen) {
          if (approxDomainHours < 500 && approxDomainHours >= 300) tickInterval = 2;
          else if (approxDomainHours >= 1300) tickInterval = 9;
        }

        const output = timeDays(startDate, endDate, tickInterval).filter(
          d => d >= timeHour.offset(timeMonth(chartData[0].x), -1),
        );

        return output;
      },
      week: () => {
        let tickInterval = isSmallScreen && approxDomainHours >= 200 ? 3 : 1;
        if (prevTimeRange.current !== 'year') return timeDays(startDate, endDate, tickInterval);
        const lastPoint = chartData[chartData.length - 1].x;
        endDate = possibleRightMostPoints[timeRange](lastPoint);
        startDate = timeHour.offset(endDate, -defaultZoomedDomainsBasedOnTimeRange[timeRange]);

        return timeDays(startDate, endDate, tickInterval);
      },
      day: () => {
        // in case the last point is precissely the beginning of another day, adding one minute so that the whole other day can render
        if (lastPoint.getHours() === 0 && lastPoint.getMinutes() === 0)
          lastPoint.setMinutes(lastPoint.getMinutes() + 1);

        if (prevTimeRange.current === 'year' || prevTimeRange.current === 'month') {
          endDate = possibleRightMostPoints[timeRange](lastPoint);
          startDate = timeHour.offset(endDate, -defaultZoomedDomainsBasedOnTimeRange[timeRange]);
        }

        let tickInterval =
          approxDomainHours > 40
            ? 8
            : approxDomainHours > 28
              ? 6
              : approxDomainHours > 16
                ? 4
                : approxDomainHours > 4
                  ? 2
                  : 1;

        if (isSmallScreen) {
          if (approxDomainHours >= 11 && approxDomainHours <= 15) tickInterval = 5;
          else if (approxDomainHours >= 25 && approxDomainHours <= 28) tickInterval = 6;
          else if (approxDomainHours >= 37 && approxDomainHours <= 40) tickInterval = 8;
          else if (approxDomainHours > 40) {
            tickInterval = 12;
          }
        }

        let output = timeHour.every(tickInterval).range(startDate, endDate);

        const lastValueOfOutput = timeDay(output[output.length - 1]);
        const actualLastValue = timeDay(chartData[chartData.length - 1].x);

        if (
          lastValueOfOutput > actualLastValue &&
          actualLastValue.getHours() !== 0 &&
          actualLastValue.getMinutes() !== 0
        )
          output.pop();
        return output;
      },
    };

    let allTickValues = allTickPossibleValues[timeRange]();

    const tickValues = allTickValues.filter(d => d >= zoomedXDomain[0] && d <= zoomedXDomain[1]);
    return tickValues;
  }, [timeRange, renderedData, zoomedXDomain]);

  const onDomainChange = domain => {
    setZoomedXDomain(domain.x);
  };

  const isDataIn = useMemo(() => {
    let isInLeft, isInRight, isInCurrentZoomedDomain;
    for (const point of renderedData) {
      const pointInTime = point.x.getTime();

      if (zoomedXDomain === undefined) continue;

      if (isInLeft === undefined && zoomedXDomain[0] !== undefined && pointInTime < zoomedXDomain[0].getTime())
        isInLeft = true;

      if (isInRight === undefined && zoomedXDomain[0] !== undefined && pointInTime > zoomedXDomain[1].getTime())
        isInRight = true;

      if (
        zoomedXDomain[0] !== undefined &&
        zoomedXDomain[1] !== undefined &&
        pointInTime >= zoomedXDomain[0].getTime() &&
        pointInTime <= zoomedXDomain[1].getTime()
      ) {
        isInCurrentZoomedDomain = true;
      }

      if (isInLeft !== undefined && isInRight !== undefined && isInCurrentZoomedDomain !== undefined) break;
    }

    isInLeft = isInLeft === undefined ? false : isInLeft;
    isInRight = isInRight === undefined ? false : isInRight;
    isInCurrentZoomedDomain = isInCurrentZoomedDomain === undefined ? false : isInCurrentZoomedDomain;

    return {
      currentZoomedDomain: isInCurrentZoomedDomain,
      leftOfZoomedDomain: isInLeft,
      rightOfZoomedDomain: isInRight,
    };
  }, [renderedData, zoomedXDomain]);

  // this will handle the time frame automatic changing
  const zoomDomainTotalHours = useMemo(() => {
    const approxHours = Math.round(Math.abs(zoomedXDomain[0] - zoomedXDomain[1]) / (1000 * 60 * 60));

    const allPosibleTimeFrames = Object.keys(timeRangeDomains);

    for (let i = 0; i < allPosibleTimeFrames.length; i++) {
      const time = allPosibleTimeFrames[i];
      if (!timeRangeDomains[time](approxHours) || time === timeRange) continue;

      const timestamp = performance.now();
      if (timestampOfLastTimeRangeChange === null) {
        setTimestampOfLastTimeRangeChange(timestamp);
        setTimeRange(time);
        break;
      }
      const diff = timestamp - timestampOfLastTimeRangeChange;
      setTimestampOfLastTimeRangeChange(timestamp);
      setTimeRange(diff <= 70 ? timeRange : time);
      break;
    }

    return approxHours;
  }, [zoomedXDomain]);

  const [activateLabel, setActivateLabel] = useState(true);
  useEffect(() => {
    setActivateLabel(false);
    delay(0.5).then(() => {
      setActivateLabel(true);
    });
  }, [zoomedXDomain]);

  const dateRangeLabel = useMemo(() => {
    let leftPoint = tickValues[0];
    let rightPoint = tickValues[tickValues.length - 1];

    if (leftPoint === undefined) leftPoint = zoomedXDomain[0];
    if (rightPoint === undefined) rightPoint = zoomedXDomain[1];
    //the second one is the right most value in the domain

    let formattedLeftDate,
      formattedRightDate = '';

    // not showing the next day when there's no data on the next day of the maximum point
    if (rightPoint > chartData[chartData.length - 1].x && timeRange === 'day')
      rightPoint = chartData[chartData.length - 1].x;

    // if the most left point in the renderedData is present it will consider its value instead of the tick value. this is to avoid displaying erroneous information. it considers both the first index and the second one because the first index usually won't show up unless you are at the left boundary and the second index is what is usually on the zoomed domain
    if (renderedData.length > 1 && zoomedXDomain.length > 1) {
      if (
        renderedData[0].x >= zoomedXDomain[0] &&
        renderedData[0].x <= zoomedXDomain[1] &&
        renderedData[0].x < leftPoint
      )
        leftPoint = renderedData[0].x;

      if (
        renderedData[1].x >= zoomedXDomain[0] &&
        renderedData[1].x <= zoomedXDomain[1] &&
        renderedData[1].x < leftPoint
      )
        leftPoint = renderedData[1].x;
      if (
        renderedData[renderedData.length - 1].x >= zoomedXDomain[0] &&
        renderedData[renderedData.length - 1].x <= zoomedXDomain[1] &&
        renderedData[renderedData.length - 1].x > rightPoint
      )
        rightPoint = renderedData[renderedData.length - 1].x;
    }

    if (timeRange === 'day' || timeRange === 'week') {
      //the first point will always not be shown so we will consider the second one
      let roundedLeftDate = timeDay(leftPoint);
      let roundedRightDate = timeDay(rightPoint);
      let leftHours = leftPoint.getHours();
      let rightHours = rightPoint.getHours();
      let leftMinutes = leftPoint.getMinutes();
      let rightMinutes = rightPoint.getMinutes();

      const sameDay =
        roundedLeftDate.getTime() === roundedRightDate.getTime() ||
        (Math.abs(roundedLeftDate.getTime() - roundedRightDate.getTime()) === 86_400_000 &&
          leftHours === 0 &&
          rightHours === 0 &&
          leftMinutes === 0 &&
          rightMinutes === 0);

      if (sameDay)
        return roundedLeftDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });

      formattedLeftDate = roundedLeftDate.toLocaleDateString(undefined, {
        year: roundedLeftDate.getFullYear() !== roundedRightDate.getFullYear() ? 'numeric' : undefined,
        month: 'long',
        day: 'numeric',
      });

      formattedRightDate = roundedRightDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (roundedRightDate < roundedLeftDate) return formattedRightDate;
    } else if (timeRange === 'month' || timeRange === 'year') {
      const sameMonth =
        leftPoint.getMonth() === rightPoint.getMonth() && leftPoint.getFullYear() === rightPoint.getFullYear();
      if (sameMonth)
        return leftPoint.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        });

      formattedLeftDate = leftPoint.toLocaleDateString(undefined, {
        year: leftPoint.getFullYear() !== rightPoint.getFullYear() ? 'numeric' : undefined,
        month: 'long',
      });

      formattedRightDate = rightPoint.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
      });
    }

    return `${formattedLeftDate} - ${formattedRightDate}`;
  }, [tickValues, timeRange]);

  const dataMutationClarification = useMemo(() => {
    if (timeRange === 'day') return '';

    let output = '';
    if (timeRange === 'week' || timeRange === 'month') output = '⚠️ Values shown are daily averages';
    else if (timeRange === 'year') output = '⚠️ Values shown are monthly averages';

    if (isLargeScreen || isSmallScreen) output = output.replace('are ', 'are\n');
    return output;
  }, [timeRange]);

  const voronoiRadius = useMemo(() => {
    const timeRanges = {
      day: 100,
      week: 150,
      month: 100,
      year: 50,
    };
    return timeRanges[timeRange] || 100;
  }, [timeRange]);

  return (
    <VictoryChart
      width={chartWidth}
      height={chartHeight - 40}
      domain={getEntireDomain}
      domainPadding={{x: domainPadding, y: 10}}
      containerComponent={
        <VictoryZoomVoronoiContainer
          zoomDimension={'x'}
          height={chartHeight}
          onZoomDomainChange={onDomainChange}
          zoomDomain={{x: zoomedXDomain}}
          minimumZoom={{x: 10_000_000}}
          onActivated={points => {
            if (points.length === 0) return;

            const thePoint = points[0];
            if (thePoint.x === selectedPoint.x && thePoint.y === selectedPoint.y) return;
            setSelectedPoint({x: thePoint.x, y: thePoint.y});
          }}
          onDeactivated={points => {
            if (selectedPoint.x === '' || points.length > 0) return;
            setSelectedPoint(nullPointValue);
          }}
          voronoiBlacklist={[
            'blacklisted',
            'blacklisted2',
            'blacklisted3',
            'blacklisted4',
            'blacklisted5',
            'blacklisted6',
            'blacklisted7',
            'blacklisted8',
          ]}
          radius={voronoiRadius}
        />
      }
      theme={VictoryTheme.material}
    >
      <VictoryLabel
        x={chartWidth / 2}
        y={30}
        text={dateRangeLabel}
        textAnchor="middle"
        name="blacklisted3"
        style={{
          fontSize: dateRangeLabelFontSizes,
          fill: 'black',
          fontFamily: 'Poppins, sans-serif',
        }}
      />

      <VictoryLabel
        x={chartWidth / 2}
        y={chartHeight - 25}
        text={dataMutationClarification}
        textAnchor="middle"
        name="blacklisted4"
        style={{
          fontSize: dataMutationClarificationFontSizes,
          fill: 'grey',
          fontFamily: 'Poppins, sans-serif',
        }}
      />

      <LabelButton
        direction="left"
        chartHeight={chartHeight}
        chartWidth={chartWidth}
        name="blacklisted5"
        isDataIn={isDataIn}
        getEntireDomain={getEntireDomain}
        timeRange={timeRange}
        chartData={chartData}
        zoomedXDomain={zoomedXDomain}
        setZoomedXDomain={setZoomedXDomain}
        baseFontSize={dataMutationClarificationFontSizes}
      />
      <LabelButton
        direction="right"
        chartHeight={chartHeight}
        chartWidth={chartWidth}
        name="blacklisted6"
        isDataIn={isDataIn}
        getEntireDomain={getEntireDomain}
        timeRange={timeRange}
        chartData={chartData}
        zoomedXDomain={zoomedXDomain}
        setZoomedXDomain={setZoomedXDomain}
        baseFontSize={dataMutationClarificationFontSizes}
      />
      <VictoryAxis
        name="blacklisted7"
        tickFormat={t => {
          const date = new Date(t);
          const formatted = formatXAxisTicksBasedOnTimeRange(timeRange, date);
          return `${formatted} `;
        }}
        style={{
          tickLabels: {
            fontSize: victoryAxesFontSizes,
          },
          grid: {
            stroke: '#aaaaaa',
            strokeDasharray: '0,7',
            strokeWidth: 2,
          },
          axis: {
            strokeWidth: 1,
            stroke: '#b7b7b7',
            strokeDasharray: 'none', // This makes the grid lines solid
          },
        }}
        tickValues={tickValues}
      />

      <VictoryAxis
        name="blacklisted8"
        dependentAxis
        style={{
          tickLabels: {
            fontSize: victoryAxesFontSizes,
            // so that range values with more than 2 digits can be displayed correctly
            ...(!isLargeScreen && {
              padding: 0,
            }),
          },
          grid: {
            strokeWidth: 1,
            stroke: '#b7b7b7',
            strokeDasharray: 'none', // This makes the grid lines solid
          },
          axis: {
            stroke: '#aaaaaa',
            strokeWidth: 2,
            strokeDasharray: '0,7',
            offset: 0,
          },
        }}
      />
      <svg style={{height: 0}}>
        <defs>
          <linearGradient id="customGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="10%" style={{stopColor: 'rgb(255,255,255)'}} />
            <stop offset="100%" style={{stopColor: 'rgba(193, 228, 255,0.8)'}} />
          </linearGradient>
        </defs>
      </svg>
      <VictoryArea
        name="blacklisted"
        data={chartData}
        style={{
          data: {
            strokeWidth: victoryScatterSizes * 0.7,
            stroke: 'black',
            fill: 'url(#customGradient)',
            opacity: 0.5,
          },
        }}
      />
      <VictoryScatter
        data={renderedData}
        size={({active}) => {
          return active && activateLabel && !isMouseDown ? victoryScatterSizes + 3 : victoryScatterSizes;
        }}
        symbol={({active}) => (active && activateLabel && !isMouseDown ? 'diamond' : 'circle')}
        style={{
          data: {
            stroke: '#35C6C9',
            fill: '#FFF',
            strokeWidth: victoryScatterSizes / 1.5,
          },
        }}
        labelComponent={
          activateLabel && !isMouseDown ? (
            <VictoryTooltip
              constrainToVisibleArea
              pointerLength={10}
              style={{
                fill: 'white',
                textAlign: 'left',
                fontSize: tooltipFontSizes,
                display: ({datum}) => {
                  return datum.x === selectedPoint.x && datum.y === selectedPoint.y ? undefined : 'none';
                },
              }}
              flyoutStyle={{
                stroke: 'grey',
                fill: 'grey',
                display: ({datum}) => {
                  return datum.x === selectedPoint.x && datum.y === selectedPoint.y ? undefined : 'none';
                },
              }}
            />
          ) : (
            <></>
          )
        }
        labels={args => {
          const datum = args.datum;
          if (datum.x < zoomedXDomain[0] || datum.x > zoomedXDomain[1]) return '';

          const value = handleFormattingByMetricKey(datum.y, metricKey);
          const date = formatVoronoiDateLabelBasedOnTimeRange(timeRange, datum.x);

          const output = r({
            xs: `Value: ${value} ${metricUnit}\n${date}`,
            md: `Value: ${value} ${metricUnit} | Date: ${date}`,
          });

          return datum.x === selectedPoint.x && datum.y === selectedPoint.y && activateLabel && !isMouseDown
            ? output
            : '';
        }}
      />
    </VictoryChart>
  );
};
